import Modal from 'antd/lib/modal/Modal';
import { useEffect, useState } from 'react';
import 'moment/locale/en-gb';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import UploadFilesDragger from '../../components/UploadFiles/UploadFilesDragger';
import { deleteMyday } from '../../fetchHooks/deleteMyday';
import { handleCreateMyday } from '../../fetchHooks/handleCreateMyday';
import { handlePublishMyday } from '../../fetchHooks/handlePublishMyday';
import { handleUpdateMyday } from '../../fetchHooks/handleUpdateMyday';
import type { MydayType } from '../../fetchHooks/useMyday';
import type { UserType } from '../../fetchHooks/useUserProfile';
import { ModalTitle } from './EditMyday';
import type { MyDayVariant } from './MyDayPage';
import { MydayForm } from './MydayForm';

interface CreateMydayProps {
  onOk?: (data: MydayType) => void;
  onClose?: () => void;
  showModal?: boolean;
  userProfile?: UserType;
  type?: MyDayVariant;
}

export const CreateMyday = ({ onOk, onClose, showModal = true, userProfile, type }: CreateMydayProps) => {
  const [newMyday, setNewMyday] = useState<MydayType | undefined>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  if (!type) {
    console.error('type is required');
    return null;
  }

  useEffect(() => {
    const defaultDate = dayjs().toDate();
    if (showModal && !newMyday) {
      handleCreateMyday({
        data: {
          userId: userProfile?.id,
          year: defaultDate.getFullYear(),
          month: defaultDate.getMonth() + 1,
          day: defaultDate.getDate(),
        },
        t,
        user: userProfile!,
        queryClient: queryClient,
        onOk: (res: MydayType) => {
          setNewMyday({ ...res });
        },
        withConfirmation: false,
      });
    }
  }, [showModal, t, newMyday, userProfile, queryClient]);

  const onFinish = (values: MydayType) => {
    const d = new Date(values.date);
    const data = {
      ...values,
      userId: userProfile?.id,
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
    // First update the myday with form data, then publish it
    handleUpdateMyday(data, newMyday!, queryClient, () => {
      // After updating, publish the myday
      newMyday?.id &&
        handlePublishMyday(newMyday.id, () => {
          onOk?.(data);
          setNewMyday(undefined);
        });
    });
  };

  if (!userProfile) return null;

  const onCancel = () => {
    console.log('onCancel');
    newMyday?.id && deleteMyday(newMyday.id, () => setNewMyday(undefined));
    onClose?.();
  };
  return (
    <Modal
      open={showModal}
      onCancel={onCancel}
      style={{ minHeight: '50vh' }}
      styles={{ body: { padding: 0, borderRadius: '20px' } }}
      className="modalStyle"
      width="1280px"
      footer={null}
      centered
      destroyOnHidden
    >
      <ModalTitle title={t('myday.createLifeLog')} />
      {newMyday && (
        <MydayForm onFinish={onFinish} onCancel={onCancel} myday={newMyday} type={type} isEdit={false}>
          <UploadFilesDragger myday={newMyday} />
        </MydayForm>
      )}
    </Modal>
  );
};
